/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Formats a date to dd-mm-yyyy format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) return '';
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date string that might already be in dd/mm/yyyy or dd-mm-yyyy format
 * @param {string} dateString - Date string in various formats
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  
  // If already in dd-mm-yyyy format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    return dateString;
  }
  
  // If in dd/mm/yyyy format, convert to dd-mm-yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString.replace(/\//g, '-');
  }
  
  // If in ISO format or other formats, parse and format
  try {
    const date = new Date(dateString);
    return formatDate(date);
  } catch (error) {
    // If all else fails, return the original string
    return dateString;
  }
};

/**
 * Converts dd-mm-yyyy format to ISO date string for form inputs
 * @param {string} ddmmyyyy - Date string in dd-mm-yyyy format
 * @returns {string} ISO date string (yyyy-mm-dd) for HTML date inputs
 */
export const convertToISODate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  
  try {
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    }
    return '';
  } catch (error) {
    console.error('Error converting to ISO date:', error);
    return '';
  }
};

/**
 * Converts ISO date string to dd-mm-yyyy format
 * @param {string} isoDate - ISO date string (yyyy-mm-dd)
 * @returns {string} Date string in dd-mm-yyyy format
 */
export const convertFromISODate = (isoDate) => {
  if (!isoDate) return '';
  
  try {
    const parts = isoDate.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}-${month}-${year}`;
    }
    return '';
  } catch (error) {
    console.error('Error converting from ISO date:', error);
    return '';
  }
};

/**
 * Gets current date in dd-mm-yyyy format
 * @returns {string} Current date in dd-mm-yyyy format
 */
export const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Converts a date string to Firebase Timestamp
 * @param {string|Date} date - Date string or Date object
 * @returns {Date|null} Date object for Firebase timestamp or null if invalid
 */
export const convertToTimestamp = (date) => {
  if (!date) return null;
  
  try {
    // If it's already a Date object, return it
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    
    // If it's a string, try to parse it
    if (typeof date === 'string') {
      // Handle dd-mm-yyyy format
      if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
        const [day, month, year] = date.split('-');
        const dateObj = new Date(year, month - 1, day);
        return isNaN(dateObj.getTime()) ? null : dateObj;
      }
      
      // Handle dd/mm/yyyy format
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        const dateObj = new Date(year, month - 1, day);
        return isNaN(dateObj.getTime()) ? null : dateObj;
      }
      
      // Handle ISO format and other standard formats
      const dateObj = new Date(date);
      return isNaN(dateObj.getTime()) ? null : dateObj;
    }
    
    return null;
  } catch (error) {
    console.error('Error converting to timestamp:', error);
    return null;
  }
};

/**
 * Converts Firebase Timestamp or Date object to display format
 * @param {Date|Object} timestamp - Firebase timestamp or Date object
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const convertFromTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    // Handle Firebase Timestamp object
    if (timestamp && typeof timestamp.toDate === 'function') {
      return formatDate(timestamp.toDate());
    }
    
    // Handle Date object
    if (timestamp instanceof Date) {
      return formatDate(timestamp);
    }
    
    // Handle timestamp in milliseconds
    if (typeof timestamp === 'number') {
      return formatDate(new Date(timestamp));
    }
    
    // Handle object with seconds property (Firestore timestamp)
    if (timestamp && timestamp.seconds) {
      return formatDate(new Date(timestamp.seconds * 1000));
    }
    
    return '';
  } catch (error) {
    console.error('Error converting from timestamp:', error);
    return '';
  }
};

/**
 * Gets current timestamp for Firebase
 * @returns {Date} Current date object
 */
export const getCurrentTimestamp = () => {
  return new Date();
};

/**
 * Converts date string to timestamp for Firebase storage
 * Handles various input formats and returns consistent Date object
 * @param {string|Date} dateInput - Date in various formats
 * @returns {Date|null} Date object or null if invalid
 */
export const prepareTimestampForFirebase = (dateInput) => {
  const timestamp = convertToTimestamp(dateInput);
  return timestamp;
};
